import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { webtoons, webtoonCategories } from '@/mocks/webtoons';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'rating' | 'views' | 'latest'>('rating');

  const currentCategory = webtoonCategories.find((c) => c.slug === slug);

  const filtered = useMemo(() => {
    let result = slug === 'all' ? [...webtoons] : webtoons.filter((w) => w.category === slug);

    switch (sortBy) {
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'views':
        result.sort((a, b) => parseInt(b.views.replace('万', '0000')) - parseInt(a.views.replace('万', '0000')));
        break;
      case 'latest':
        result.sort((a, b) => b.chapters[0]?.date.localeCompare(a.chapters[0]?.date || '') || 0);
        break;
    }
    return result;
  }, [slug, sortBy]);

  const pageTitle = currentCategory
    ? `${currentCategory.name}漫画 - 韩漫网`
    : '全部分类 - 韩漫网';
  const pageDesc = currentCategory
    ? `在线免费阅读${currentCategory.name}类韩国漫画，资源齐全更新及时。韩漫网为您精选优质${currentCategory.name}韩漫。`
    : '在线免费阅读全部韩国漫画，恋爱、奇幻、动作、悬疑等多种类型任您选择。韩漫网是您探索漫画世界的完美伙伴。';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDesc,
    url: `https://qmss2.cn/category/${slug || 'all'}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-6 md:py-8">
        <div className="mb-4">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: currentCategory?.name || '全部' },
            ]}
          />
        </div>

        <div className="mb-6">
          <h1 className="font-heading text-2xl md:text-3xl text-foreground-950">
            {currentCategory ? `${currentCategory.name}漫画` : '全部漫画'}
          </h1>
          <p className="text-sm text-foreground-600 mt-2">
            共 {filtered.length} 部作品
          </p>
        </div>

        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <span className="text-sm text-foreground-600 whitespace-nowrap">分类：</span>
          <div className="flex items-center gap-1 flex-wrap">
            {webtoonCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                className={`px-3 py-1.5 text-sm rounded-full transition-colors whitespace-nowrap ${
                  (slug || 'all') === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2 mb-6">
          <span className="text-sm text-foreground-600 whitespace-nowrap">排序：</span>
          {[
            { key: 'rating' as const, label: '评分最高' },
            { key: 'views' as const, label: '人气最热' },
            { key: 'latest' as const, label: '最近更新' },
          ].map((opt) => (
            <button
              key={opt.key}
              onClick={() => setSortBy(opt.key)}
              className={`px-4 py-1.5 text-sm rounded-full transition-colors whitespace-nowrap cursor-pointer ${
                sortBy === opt.key
                  ? 'bg-foreground-900 text-background-50'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
          {filtered.map((webtoon) => (
            <Link
              key={webtoon.id}
              to={`/article/${webtoon.id}`}
              className="group cursor-pointer"
              data-product-shop
            >
              <div className="relative aspect-[3/4] rounded-lg overflow-hidden mb-3 bg-background-200">
                <img
                  src={webtoon.cover}
                  alt={webtoon.title}
                  title={`在线韩漫 ${webtoon.title}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <div className="absolute top-2 left-2">
                  <span className="px-2 py-0.5 text-xs font-medium bg-primary-500 text-background-50 rounded-full whitespace-nowrap">
                    {webtoon.rating}
                  </span>
                </div>
                <div className="absolute top-2 right-2">
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap ${
                    webtoon.status === '连载中'
                      ? 'bg-accent-500 text-background-50'
                      : 'bg-secondary-500 text-background-50'
                  }`}>
                    {webtoon.status}
                  </span>
                </div>
              </div>
              <h3 className="text-sm md:text-base font-medium text-foreground-900 group-hover:text-primary-500 transition-colors truncate">
                {webtoon.title}
              </h3>
              <p className="text-xs text-foreground-600 mt-1">{webtoon.author}</p>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                {webtoon.tags.slice(0, 2).map((tag) => (
                  <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-1.5 py-0.5 rounded whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
            </Link>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-20">
            <p className="text-foreground-600">该分类暂无漫画作品</p>
          </div>
        )}
      </div>
    </>
  );
}